import TestFilterTabs from '../generic/TestFilterTabs';
import QualityDefinitions from './QualityDefinitions';
import QualitySummary from './QualitySummary';
import QualityTestBreakdown from './QualityTestBreakdown';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPackQualityOfAttempt, setPage } from '../../../../../apps/frontend/src/redux/slices/packSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import AttemptAnalysisLegend from '../../components/analysis/AttemptAnalysisLegend';
import { ArrowLeft } from '@phosphor-icons/react';
import PackPagination from '../generic/PackPagination';
import PackButton from '../generic/PackButton';

const QualityOfAttempt = ({ pack, packId, onResetFilters }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSubject, setActiveSubject] = useState('overall');

  // Scroll states
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);
  const scrollContainerRef = useRef(null);

  const {
    qualityOfAttempt: { data, loading },
    filters,
  } = useSelector((state) => state.pack);

  // ✅ Refetch on page OR filter change
  useEffect(() => {
    if (!packId) return;
    dispatch(
      fetchPackQualityOfAttempt({
        packId,
        page: filters?.page || 1,
        testFilter: filters?.testFilter,
      }),
    );
  }, [dispatch, packId, filters?.page, filters?.testFilter]);

  useEffect(() => {
    // Handle URL params for initial filter setting
    const params = new URLSearchParams(location.search);
    const count = params.get('count');
    if (count) {
      let filterValue = 'last';
      if (count === '1') filterValue = 'last';
      else if (count === '3') filterValue = 'last3';
      else if (count === '5') filterValue = 'last5';
      else if (count === '10') filterValue = 'last10';
      else if (count === 'all') filterValue = 'all';
      // ✅ Instead of local state, you could dispatch setFilter here if needed
    }
  }, [location.search]);

  // Hide-on-scroll logic
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isTabletOrBelow = window.innerWidth < 1024; // lg breakpoint

      if (!isTabletOrBelow) {
        // Desktop: hide description when scrolling down, show at top
        if (currentScrollY <= 0) {
          if (!isDescriptionVisible) setIsDescriptionVisible(true);
        } else if (currentScrollY > 70 && isDescriptionVisible) {
          setIsDescriptionVisible(false);
        } else if (currentScrollY < 30 && !isDescriptionVisible) {
          setIsDescriptionVisible(true);
        }
        setIsHeaderVisible(true);
        return;
      }

      // Mobile/Tablet: hide header when scrolling down
      if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  const breadcrumbItems = ['Jee Main Test Series', 'Pack Analysis', 'Quality of Attempts'];

  const totalPages = data?.totalPagesAvailable || 1;
  const activePage = filters?.page || 1;

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col h-screen overflow-y-auto" ref={scrollContainerRef}>
      {/* Header with smooth animation */}
      <div
        className={`z-10 q3-glass rounded transition-transform duration-300 ease-in-out lg:translate-y-0 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="p-6 pb-4">
          {/* Mobile Back Button */}
          <PackButton navigateTo="/v2/home" />

          <div className="mb-6">
            <h1 className="text-3xl font-bold text-[var(--q3-neutral-default)]">Quality of Attempts</h1>
          </div>

          <TestFilterTabs />
        </div>
      </div>

      <div className="p-5 -mt-4">
        <AttemptAnalysisLegend />
      </div>

      {/* Scrollable Content Section */}
      <div className="p-6 pt-2 space-y-8">
        <QualitySummary
          data={data?.summary}
          loading={loading}
          subjects={pack?.subjects}
          onResetFilters={onResetFilters}
        />

        <QualityTestBreakdown
          data={data?.testWiseQoA}
          loading={loading}
          activeSubject={activeSubject}
          onSubjectChange={setActiveSubject}
          subjects={pack?.subjects}
          onResetFilters={onResetFilters}
        />

        {/* Pagination only at bottom */}
        {totalPages > 1 && (
          <PackPagination
            totalPages={totalPages}
            activePage={activePage}
            onPageClick={(page) => dispatch(setPage(page))}
          />
        )}
      </div>
    </div>
  );
};

export default QualityOfAttempt;
